import React, { ElementType, ReactNode } from 'react';
import {
  Avatar,
  Box,
  ListItem,
  Tooltip,
  Icons,
  IconButton,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './SearchListItem.styles';

type SearchListItemProps = {
  fullName?: string;
  email?: string;
  avatar?: string | null;
  onActionClick?: () => void;
  actionIcon?: ElementType | null;
  tooltip?: string;
  userAdded?: boolean;
  isDisabled?: boolean;
  disabledText?: ReactNode;
  styles?: any;
};

type OverflowTextProps = {
  text: string;
  children?: ReactNode;
  sx?: Record<string, unknown>;
};

const OverflowText = ({ text, children, sx }: OverflowTextProps) => {
  const [textRef, isOverflowing] = useIsOverflowing<HTMLSpanElement>();
  const styles = createStyles();

  return (
    <Tooltip
      title={text}
      position="top"
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      <Box
        component="span"
        ref={textRef}
        sx={{
          ...styles.overflowText,
          ...sx,
        }}
      >
        {children ?? text}
      </Box>
    </Tooltip>
  );
};

const SearchListItem = ({
  fullName = '',
  email = '',
  avatar = null,
  onActionClick,
  actionIcon: ActionIcon = Icons.AddCircle,
  tooltip = 'Add to extension',
  userAdded = false,
  isDisabled = false,
  disabledText,
  styles: stylesOverride = {},
}: SearchListItemProps) => {
  const theme = useTheme();
  const shouldGreyOut = userAdded || isDisabled;
  const styles = createStyles(theme, shouldGreyOut);

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onActionClick) {
      return;
    }
    event.stopPropagation();
    onActionClick();
  };

  const actionLabel = [tooltip, fullName].filter(Boolean).join(', ');

  const actionButton =
    !isDisabled && ActionIcon ? (
      <Box component="span" sx={styles.actionButtonWrapper}>
        <IconButton
          aria-label={actionLabel}
          sx={styles.actionIconButton}
          size="small"
          color="primary"
          onClick={handleActionClick}
        >
          <Tooltip title={tooltip} position="top">
            <ActionIcon fontSize="small" sx={styles.actionIcon} />
          </Tooltip>
        </IconButton>
      </Box>
    ) : null;

  const content = (
    <Box
      sx={{
        ...styles.listItem,
        '& .MuiListItem-root': {
          ...styles.listItemRoot,
          ...stylesOverride?.rootSx,
        },
      }}
    >
      <ListItem
        component="div"
        size="medium"
        textTruncate
        leftDecoration={
          <Avatar
            src={avatar || undefined}
            altText=""
            aria-hidden
            fallbackText={fullName?.charAt(0)?.toUpperCase() || 'U'}
            sx={styles.avatar}
          />
        }
        primaryText={<OverflowText text={fullName} />}
        secondaryText={email ? <OverflowText text={email} /> : undefined}
        rightDecoration={actionButton}
      />
    </Box>
  );

  if (isDisabled && disabledText) {
    return (
      <Tooltip title={disabledText} position="top">
        <Box sx={styles.disabledWrapper}>{content}</Box>
      </Tooltip>
    );
  }

  return content;
};

export default React.memo(SearchListItem);
